import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import {
	BriefcaseIcon,
	CarIcon,
	HomeIcon,
	type LucideIcon,
	MessageCircleIcon,
	PlusIcon,
	ShieldIcon,
	TrendingUpIcon,
} from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { CtaCard } from "web_myplann/Shared/Components/Redesign/CtaCard";
import { RedesignListBackground } from "web_myplann/Shared/Components/Redesign/RedesignListBackground";
import { device, NavigationTypes } from "web_myplann/Utils/device";

type PreviewCard = {
	icon: LucideIcon;
	color: string;
	bg: string;
	labelKey: keyof typeof Resources.Portfolio.List.Redesign.EmptyState.PreviewCards;
	subKey: keyof typeof Resources.Portfolio.List.Redesign.EmptyState.PreviewCards;
};

type Props = {
	hasPortfolioRight: boolean;
};

export const PortfolioEmptyState: FC<Props> = ({ hasPortfolioRight }) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const hasGoalsRight = useGoalsRightOptional();

	const handleAdvisorClick = () => {
		if (hasGoalsRight) {
			navigate(Routing.Chat.List.path());
		} else {
			device.navigationRequest(NavigationTypes.ChatScreen);
		}
	};

	const previewCards: PreviewCard[] = [
		{
			icon: ShieldIcon,
			color: theme.palette.green[600],
			bg: theme.palette.green[50],
			labelKey: "insuranceLabel",
			subKey: "insuranceSub",
		},
		{
			icon: TrendingUpIcon,
			color: theme.palette.purple[600],
			bg: theme.palette.purple[50],
			labelKey: "investmentLabel",
			subKey: "investmentSub",
		},
		{
			icon: HomeIcon,
			color: theme.palette.blue[600],
			bg: theme.palette.blue[50],
			labelKey: "realEstateLabel",
			subKey: "realEstateSub",
		},
		{
			icon: CarIcon,
			color: theme.palette.amber[600],
			bg: theme.palette.amber[50],
			labelKey: "assetsLabel",
			subKey: "assetsSub",
		},
	];

	const previewCopy = Resources.Portfolio.List.Redesign.EmptyState.PreviewCards;

	return (
		<RedesignListBackground px={2.5} pt={3}>
			<Stack alignItems="center" mb={2.5}>
				<Stack
					alignItems="center"
					justifyContent="center"
					sx={{
						width: 80,
						height: 80,
						borderRadius: "24px",
						background: theme.gradients.iconBadge,
					}}
				>
					<BriefcaseIcon size={40} color={theme.palette.darkmodeText.white} />
				</Stack>
			</Stack>

			<Typography variant="h3" textAlign="center" mb={0.75}>
				{t(Resources.Portfolio.List.Redesign.EmptyState.headline)}
			</Typography>
			<Typography variant="body2" textAlign="center" mb={3}>
				{t(Resources.Portfolio.List.Redesign.EmptyState.description)}
			</Typography>

			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: "repeat(2, 1fr)",
					gap: 1.25,
					mb: 3,
				}}
			>
				{previewCards.map((card) => (
					<Stack
						key={card.labelKey}
						sx={{ borderRadius: "16px", p: 1.75, bgcolor: card.bg }}
					>
						<card.icon
							size={theme.iconSize.md}
							color={card.color}
							style={{ marginBottom: 8 }}
						/>
						<Typography variant="bodyStrong">
							{t(previewCopy[card.labelKey])}
						</Typography>
						<Typography variant="caption" color={theme.palette.grey[500]}>
							{t(previewCopy[card.subKey])}
						</Typography>
					</Stack>
				))}
			</Box>

			{hasPortfolioRight && (
				<Button
					variant="primary"
					startIcon={<PlusIcon size={18} />}
					onClick={() => navigate(Routing.Portfolio.PortfolioAddItem.path())}
					sx={{ mb: 1.5 }}
				>
					{t(Resources.Portfolio.List.Redesign.EmptyState.primaryCta)}
				</Button>
			)}

			<CtaCard
				iconBg={theme.palette.blue[100]}
				title={t(Resources.Portfolio.List.Redesign.EmptyState.advisorTitle)}
				description={t(
					Resources.Portfolio.List.Redesign.EmptyState.advisorDescription,
				)}
				onClick={handleAdvisorClick}
				icon={
					<MessageCircleIcon
						size={theme.iconSize.md}
						color={theme.palette.blue[600]}
					/>
				}
			/>
		</RedesignListBackground>
	);
};
